// src/components/Login.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';
import { Code, Paintbrush, Layout } from 'lucide-react';

const ContentPreferenceToggle = ({ preference, onPreferenceChange }) => {
  const options = [
    { id: 'creatie', label: 'Creatie', Icon: Paintbrush, color: 'bg-[#e8c2d9]' },
    { id: 'development', label: 'Development', Icon: Code, color: 'bg-[#3cdc96]' },
    { id: 'all', label: 'Alles', Icon: Layout, color: 'bg-[#82e6e6]' }
  ];

  return (
    <div className="space-y-2 w-full">
      <label className="block text-gray-400 text-sm mb-2">
        Contentvoorkeur:
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map(({ id, label, Icon, color }) => (
          <button
            key={id}
            type="button"
            onClick={() => onPreferenceChange(id)}
            className={`
              relative h-10 px-4 rounded-lg text-sm font-medium transition-all flex items-center gap-2
              bg-[#383838] text-gray-300 hover:bg-[#484848]
              ${preference === id && 'before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-[#e8c2d9]'}
            `}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Login = ({ onLoginSuccess }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [contentPreference, setContentPreference] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [logoVisible, setLogoVisible] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [moveLogoUp, setMoveLogoUp] = useState(false);

  // Check URL parameters for auto-login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlName = params.get('naam');
    const urlPassword = params.get('password');
    
    if (urlName && urlPassword === 'teamai') {
      setName(urlName);
      setPassword(urlPassword);
      // Remove params from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Auto login
      localStorage.setItem('contributorName', urlName);
      Cookies.set('isLoggedIn', 'true', { expires: 30 });
      onLoginSuccess();
    }
  }, [onLoginSuccess]);

  // Logo animation sequence
  useEffect(() => {
    const sequence = async () => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLogoVisible(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowAI(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      setMoveLogoUp(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setShowForm(true);
    };
    sequence();
  }, []);

  // Original logo dimensions
  const ORIGINAL = {
    LOGO: { width: 363, height: 236 },
    AI: { width: 137, height: 236 }
  };

  const SCALE_FACTOR = 107 / ORIGINAL.LOGO.height;

  const SCALED = {
    LOGO: {
      width: Math.round(ORIGINAL.LOGO.width * SCALE_FACTOR),
      height: 107
    },
    AI: {
      width: Math.round(ORIGINAL.AI.width * SCALE_FACTOR),
      height: 107
    }
  };

  const TOTAL_WIDTH = SCALED.LOGO.width + SCALED.AI.width;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (password === "teamai") {
        localStorage.setItem('contributorName', name);
        Cookies.set('isLoggedIn', 'true', { expires: 30 });
        Cookies.set('contentPreference', contentPreference, { expires: 30 });
        
        // Set initial theme filters based on preference
        if (contentPreference !== 'all') {
          const initialThemeFilters = contentPreference === 'development' 
            ? ['Development']
            : ['Creatie'];
          Cookies.set('activeThemeFilters', JSON.stringify(initialThemeFilters), { expires: 30 });
        }
        
        onLoginSuccess();
      } else {
        setError('Incorrect password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Er is een fout opgetreden');
    }
  };

  return (
    <div className="fixed inset-0 bg-[#282828] flex items-center justify-center">
      <motion.div
        className="relative flex flex-col items-center w-[320px]"
        style={{ height: '420px' }}
        animate={{ 
          y: moveLogoUp ? -40 : 0
        }}
        transition={{ 
          duration: 0.8,
          ease: "easeOut"
        }}
      >
        {/* Logo container */}
        <div className="relative w-full" style={{ height: '107px' }}>
          <div className="flex items-center justify-center">
            <div 
              className="relative"
              style={{ 
                width: `${TOTAL_WIDTH}px`,
                height: '107px'
              }}
            >
              {/* Base logo part */}
              <motion.img
                src="https://www.unframed.nl/app/uploads/2025/01/logo-logo-part.png"
                alt="Unframed"
                className="absolute left-0"
                style={{ 
                  width: `${SCALED.LOGO.width}px`,
                  height: '107px',
                  objectFit: 'contain'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: logoVisible ? 1 : 0 }}
                transition={{ duration: 0.8 }}
              />
              {/* AI part */}
              <motion.img
                src="https://www.unframed.nl/app/uploads/2025/01/logo-ai-part.png"
                alt="AI"
                className="absolute"
                style={{ 
                  left: `${SCALED.LOGO.width}px`,
                  width: `${SCALED.AI.width}px`,
                  height: '107px',
                  objectFit: 'contain'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: showAI ? 1 : 0 }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>
        </div>

        {/* Form container */}
        <div className="absolute top-[185px] w-full">
          <AnimatePresence>
            {showForm && (
              <motion.form
                onSubmit={handleSubmit}
                className="w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Naam"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full px-5 py-3 rounded-lg bg-[#202020] border border-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-[#e8c2d9] text-lg"
                    />
                  </div>
                  <div>
                    <input
                      type="password"
                      placeholder="Wachtwoord"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-5 py-3 rounded-lg bg-[#202020] border border-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-[#e8c2d9] text-lg"
                    />
                  </div>
                  
                  <ContentPreferenceToggle 
                    preference={contentPreference}
                    onPreferenceChange={setContentPreference}
                  />

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}
                  <div className="relative group mt-5">
                    <button
                      type="submit"
                      className="relative w-full px-5 py-3 rounded-lg overflow-hidden 
                        bg-[#e8c2d9] text-black 
                        hover:bg-[#d6a8c1] 
                        transition-colors duration-300 
                        font-['Recoleta-SemiBold'] text-lg"
                    >
                      Ga verder
                    </button>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;